/**
 * Automations domain — React Query hooks.
 *
 * Read hooks are re-exported from the generated client (plus the generated
 * client *functions* for the hand-written queries in consumers that need a
 * custom query key / param shape).
 *
 * Write hooks are wrapped via `useInvalidatingMutation` so cache
 * invalidation is declarative and compile-time-enforced via the
 * `AutomationsMutationId` registry in `./invalidation`. The multipart
 * create/update wrappers call the imperative `FormData` helpers in
 * `./utils`.
 */

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import { createAutomationMultipart, updateAutomationMultipart } from './utils'

// ── Generated read hooks + client fns + query keys ─────────────────

export {
  useListAutomations,
  listAutomationsQueryKey,
  useGetAutomation,
  getAutomationQueryKey,
  useCheckAutomationCycles,
  checkAutomationCyclesQueryKey,
} from '../generated/hooks/automationsController'

// Generated client functions, for consumers that keep a hand-written
// `useQuery` (custom legacy query key / param shape) or fetch imperatively.
export { listAutomations } from '../generated/clients/listAutomations'
export { getAutomation } from '../generated/clients/getAutomation'

// ── Generated query/mutation parameter & response type re-exports ──

export type {
  ListAutomationsQueryResponse,
  ListAutomationsQueryParams,
} from '../generated/types/ListAutomations'
export type {
  GetAutomationQueryResponse,
  GetAutomationPathParams,
} from '../generated/types/GetAutomation'
export type {
  CreateAutomationMutationRequest,
  CreateAutomationMutationResponse,
} from '../generated/types/CreateAutomation'
export type {
  UpdateAutomationMutationRequest,
  UpdateAutomationMutationResponse,
  UpdateAutomationPathParams,
} from '../generated/types/UpdateAutomation'
export type {
  PartialUpdateAutomationMutationRequest,
  PartialUpdateAutomationMutationResponse,
  PartialUpdateAutomationPathParams,
} from '../generated/types/PartialUpdateAutomation'
export type {
  DeleteAutomationMutationResponse,
  DeleteAutomationPathParams,
} from '../generated/types/DeleteAutomation'
export type {
  DeleteAutomationAttachmentMutationResponse,
  DeleteAutomationAttachmentPathParams,
} from '../generated/types/DeleteAutomationAttachment'
export type { CycleValidationRequestRequest } from '../generated/types/CycleValidationRequestRequest'
export type { ValidateNewAutomationResponse } from '../generated/types/ValidateNewAutomationResponse'
export type { ValidateAutomationUpdateResponse } from '../generated/types/ValidateAutomationUpdateResponse'
export type { CycleAnalysisResponse } from '../generated/types/CycleAnalysisResponse'

// ── Internal imports for mutation wrappers ─────────────────────────

import { createAutomation } from '../generated/clients/createAutomation'
import { updateAutomation } from '../generated/clients/updateAutomation'
import { partialUpdateAutomation } from '../generated/clients/partialUpdateAutomation'
import { deleteAutomation } from '../generated/clients/deleteAutomation'
import { deleteAutomationAttachment } from '../generated/clients/deleteAutomationAttachment'

import type { CreateAutomationMutationRequest } from '../generated/types/CreateAutomation'
import type {
  UpdateAutomationMutationRequest,
  UpdateAutomationPathParams,
} from '../generated/types/UpdateAutomation'
import type {
  PartialUpdateAutomationMutationRequest,
  PartialUpdateAutomationPathParams,
} from '../generated/types/PartialUpdateAutomation'
import type { DeleteAutomationPathParams } from '../generated/types/DeleteAutomation'
import type { DeleteAutomationAttachmentPathParams } from '../generated/types/DeleteAutomationAttachment'
import type { AutomationMultipartPayload } from 'models/automation'

// ── Mutation hooks with auto-invalidation (WRITE) ──────────────────

/** Create an automation (JSON path). */
export function useCreateAutomation() {
  return useInvalidatingMutation('createAutomation', invalidationRegistry, {
    mutationFn: (variables: { data: CreateAutomationMutationRequest }) =>
      createAutomation(variables.data),
  })
}

/** Create an automation with file attachments (multipart path). */
export function useCreateAutomationMultipart() {
  return useInvalidatingMutation(
    'createAutomationMultipart',
    invalidationRegistry,
    {
      mutationFn: (variables: { data: AutomationMultipartPayload }) =>
        createAutomationMultipart(variables.data),
    }
  )
}

/** Update an automation (JSON path). */
export function useUpdateAutomation() {
  return useInvalidatingMutation('updateAutomation', invalidationRegistry, {
    mutationFn: (variables: {
      id: UpdateAutomationPathParams['id']
      data: UpdateAutomationMutationRequest
    }) => updateAutomation({ id: variables.id }, variables.data),
  })
}

/** Update an automation with file attachments (multipart path). */
export function useUpdateAutomationMultipart() {
  return useInvalidatingMutation(
    'updateAutomationMultipart',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        id: number
        data: AutomationMultipartPayload
      }) => updateAutomationMultipart(variables.id, variables.data),
    }
  )
}

/** Partially update an automation (e.g. toggle `is_active`). */
export function usePartialUpdateAutomation() {
  return useInvalidatingMutation(
    'partialUpdateAutomation',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        id: PartialUpdateAutomationPathParams['id']
        data: PartialUpdateAutomationMutationRequest
      }) => partialUpdateAutomation({ id: variables.id }, variables.data),
    }
  )
}

/** Delete an automation. */
export function useDeleteAutomation() {
  return useInvalidatingMutation('deleteAutomation', invalidationRegistry, {
    mutationFn: (variables: { id: DeleteAutomationPathParams['id'] }) =>
      deleteAutomation({ id: variables.id }),
  })
}

/** Delete an automation attachment. */
export function useDeleteAutomationAttachment() {
  return useInvalidatingMutation(
    'deleteAutomationAttachment',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        automation_pk: DeleteAutomationAttachmentPathParams['automation_pk']
        id: DeleteAutomationAttachmentPathParams['id']
      }) =>
        deleteAutomationAttachment({
          automation_pk: variables.automation_pk,
          id: variables.id,
        }),
    }
  )
}
